import { getAuditTrailData, extractVersionStructure } from '../../../../util/getAuditTrailData';
import { generateNodeIndexing } from '../../../../hooks/useNodeIndexing';
import { generateTitlesFromStructure, uploadJsonToS3 } from '../../../../util/AppUtils';

const CHILD_NODE_ID_FIELDS = [
  'attachment_tree_node_id',
  'video_tree_node_id',
  'rich_text_node_id',
  'canvas_tree_node_id',
  'form_tree_node_id',
];

const getSiblingOrderNumber = (node) => {
  if (!node) {
    return 0;
  }
  const order =
    node.sibling_order ??
    node.procedure_tree?.sibling_order ??
    node.detail?.procedure_tree?.sibling_order ??
    '0';
  const parsed = parseInt(order, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getChildNodeId = (child) => {
  if (!child) {
    return null;
  }
  if (child.tree_node_id) {
    return child.tree_node_id;
  }
  const detail = child.detail || {};
  for (const field of CHILD_NODE_ID_FIELDS) {
    if (detail[field]) {
      return detail[field];
    }
  }
  return null;
};

const normalizeChildType = (type = '') => {
  const lowerType = type.toLowerCase();
  if (lowerType === 'canva') {
    return 'canvas';
  }
  if (lowerType === 'richtext') {
    return 'rich-text';
  }
  return lowerType;
};

const getChildTypeDisplayName = (type = '') => {
  const normalized = normalizeChildType(type);
  const typeMap = {
    attachment: 'Attachment',
    canvas: 'Canvas',
    'rich-text': 'Rich Text',
    video: 'Video',
    form: 'Form',
  };
  if (typeMap[normalized]) {
    return typeMap[normalized];
  }
  if (!normalized) {
    return 'Element';
  }
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const toLowerCaseFirst = (value) => {
  if (!value) {
    return value;
  }
  return value.charAt(0).toLowerCase() + value.slice(1);
};

// Helper to set sibling order on a node
const setSiblingOrder = (node, index) => {
  if (!node || index === undefined || index === null) return;
  const order = index.toString();
  node.sibling_order = order;
  if (node.procedure_tree && typeof node.procedure_tree === 'object') {
    node.procedure_tree.sibling_order = order;
  }
};

// Helper to normalize sibling orders for substeps
const normalizeSubstepOrders = (substeps = []) => {
  if (!Array.isArray(substeps)) return;
  substeps.forEach((substep, index) => {
    setSiblingOrder(substep, index);
    if (Array.isArray(substep.children)) {
      substep.children.forEach((child, childIndex) => setSiblingOrder(child, childIndex));
    }
  });
};

// Helper to normalize sibling orders for steps
const normalizeStepOrders = (steps = []) => {
  if (!Array.isArray(steps)) return;
  steps.forEach((step, stepIndex) => {
    setSiblingOrder(step, stepIndex);
    if (Array.isArray(step.children)) {
      step.children.forEach((child, childIndex) => setSiblingOrder(child, childIndex));
    }
    if (Array.isArray(step.substeps)) {
      normalizeSubstepOrders(step.substeps);
    }
  });
};

// Helper to normalize sibling orders for the entire document
const normalizeDocumentSiblingOrders = (sections = []) => {
  if (!Array.isArray(sections)) return;
  sections.forEach((section, sectionIndex) => {
    if (!section) return;
    setSiblingOrder(section, sectionIndex);
    if (Array.isArray(section.steps)) {
      normalizeStepOrders(section.steps);
    }
    if (Array.isArray(section.children)) {
      section.children.forEach((child, childIndex) => setSiblingOrder(child, childIndex));
    }
  });
};

const cloneStructureForIndexing = (structure = []) => {
  if (!Array.isArray(structure)) return [];
  return structuredClone(structure);
};

const setBlakSubstep = (structure = [], sourceSubstep) => {
  if (!sourceSubstep || !Array.isArray(structure)) return structure || [];
  const substepId = sourceSubstep.tree_node_id || sourceSubstep.substep_id_ref;
  if (!substepId) return structure;

  structure = structure.map(section => {
    section.steps = section.steps.map(step => {
      step.substeps = step.substeps.map(substep => {
        if(substep.tree_node_id === substepId || substep.substep_id_ref === substepId){
          return {
            ...substep,
            children: [],
          }
        }
        return substep
      })
      return step
    })
    return section
  });

  // normalizeDocumentSiblingOrders(structure);
  return structure;
};

/**
 * Get substep letter (A, B, C, etc.) based on its index within the step
 * @param {Array} allNodes - All nodes from the document
 * @param {Object} substep - The substep object
 * @returns {string} - Substep letter (A, B, C, etc.)
 */
const getSubstepLetter = (allNodes, substep) => {
  if (!allNodes || !substep || !substep.step_id_ref) return '';
  
  // Sort sections by sibling_order
  const sortedSections = [...allNodes].sort((a, b) => {
    const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
    const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
    return orderA - orderB;
  });
  
  // Find the step containing this substep
  for (const section of sortedSections) {
    const steps = section.steps || [];
    for (const step of steps) {
      if (step.step_id_ref === substep.step_id_ref || step.tree_node_id === substep.step_id_ref) {
        const substeps = step.substeps || [];
        // Sort substeps by sibling_order
        const sortedSubsteps = [...substeps].sort((a, b) => {
          const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
          const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
          return orderA - orderB;
        });
        
        // Find substep index
        const substepIndex = sortedSubsteps.findIndex(
          s => s.substep_id_ref === substep.substep_id_ref || s.tree_node_id === substep.tree_node_id
        );
        
        if (substepIndex !== -1) {
          return String.fromCharCode(65 + substepIndex); // A, B, C, ...
        }
        return '';
      }
    }
  }
  
  return '';
};

const collectSubstepHierarchyNodes = (substep) => {
  const nodes = [];
  if (!substep) {
    return nodes;
  }
  const substepId = substep.tree_node_id || substep.substep_id_ref;
  if (substepId) {
    nodes.push({
      id: substep.tree_node_id || substepId,
      node: substep,
      type: 'substep',
      substepId,
    });
  }

  const sortedChildren = [...(substep.children || [])].sort(
    (a, b) => getSiblingOrderNumber(a) - getSiblingOrderNumber(b)
  );

  sortedChildren.forEach((child) => {
    if (child?.is_deleted) {
      return;
    }
    const childId = getChildNodeId(child);
    if (!childId) {
      return;
    }
    nodes.push({
      id: childId,
      node: child,
      type: 'substepChild',
      parentSubstepId: substepId,
    });
  });

  return nodes;
};

const resolveNodeNameWithFallback = (id, originalIndexMap, node, defaultLabel) => {
  if (id && originalIndexMap && originalIndexMap[id]) {
    return originalIndexMap[id];
  }
  if (node) {
    const possibleFields = [
      'title',
      'name',
      'label',
      'display_name',
      'substep_name',
      'step_name',
      'nodeTitle',
    ];
    for (const field of possibleFields) {
      if (node[field]) {
        return node[field];
      }
    }
    if (node.detail) {
      const detailFields = ['title', 'name', 'label', 'display_name'];
      for (const field of detailFields) {
        if (node.detail[field]) {
          return node.detail[field];
        }
      }
    }
  }
  return defaultLabel;
};

const formatSubstepDestinationName = ({
  type,
  finalStepName,
  finalSubstepName,
}) => {
  if (type === 'substep') {
    return toLowerCaseFirst(finalStepName);
  }
  if (type === 'substepChild') {
    return finalSubstepName || toLowerCaseFirst(finalStepName);
  }
  return finalStepName;
};

const stripUnaddedChildrenForSubstepCopy = (node, type) => {
  if (!node) return null;
  const cloned = JSON.parse(JSON.stringify(node));
  if (type === 'substep') {
    delete cloned.children;
  }
  return cloned;
};

const findSectionInStructure = (structure = [], sectionId) => {
  if (!sectionId) return null;
  return structure.find(section =>
    section?.tree_node_id === sectionId || section?.section_id_ref === sectionId
  ) || null;
};

const findStepInSection = (section, stepId) => {
  if (!section || !stepId) return null;
  return (section.steps || []).find(step =>
    step?.tree_node_id === stepId || step?.step_id_ref === stepId
  ) || null;
};

const matchSubstepById = (substeps = [], substepId) => {
  if (!Array.isArray(substeps) || !substepId) return null;
  return substeps.find(substep =>
    substep?.tree_node_id === substepId || substep?.substep_id_ref === substepId
  ) || null;
};

const findSubstepInStructure = (structure = [], substepId) => {
  if (!substepId) return null;
  for (const section of structure) {
    for (const step of section?.steps || []) {
      const match = matchSubstepById(step?.substeps, substepId);
      if (match) return match;
    }
  }
  return null;
};

const determineSubstepInsertIndex = (substeps = [], position, movePosition) => {
  if (!Array.isArray(substeps) || substeps.length === 0 || position === 'into') {
    return substeps?.length || 0;
  }

  const targetId = movePosition?.targetId;
  if (!targetId) return substeps.length;

  const targetIndex = substeps.findIndex(substep =>
    substep?.tree_node_id === targetId || substep?.substep_id_ref === targetId
  );

  if (targetIndex === -1) return substeps.length;
  return position === 'above' ? targetIndex : targetIndex + 1;
};

const addNodeToSubstepStructure = ({
  currentStructure = [],
  hierarchyNode,
  finalSection,
  finalStep,
  finalSubstep,
  position,
  movePosition,
  isCopyOperation = false,
  sourceSubstep,
  newCopiedSubstepTreeNodeId = null,
}) => {
  const clonedStructure = JSON.parse(JSON.stringify(currentStructure || []));
  const sectionId = finalSection?.tree_node_id || finalSection?.section_id_ref;
  const stepId = finalStep?.tree_node_id || finalStep?.step_id_ref;
  if (!sectionId || !stepId) return clonedStructure;

  let targetSection = findSectionInStructure(clonedStructure, sectionId);
  if (!targetSection) {
    targetSection = {
      tree_node_id: finalSection?.tree_node_id || null,
      section_id_ref: finalSection?.section_id_ref || null,
      version_id_ref: finalSection?.version_id_ref,
      steps: [],
    };
    clonedStructure.push(targetSection);
    normalizeDocumentSiblingOrders(clonedStructure);
  }

  if (!Array.isArray(targetSection.steps)) targetSection.steps = [];
  let targetStep = findStepInSection(targetSection, stepId);
  if (!targetStep) {
    targetStep = {
      tree_node_id: finalStep?.tree_node_id || null,
      step_id_ref: finalStep?.step_id_ref || null,
      version_id_ref: finalStep?.version_id_ref,
      substeps: [],
    };
    targetSection.steps.push(targetStep);
    normalizeStepOrders(targetSection.steps);
  }

  // Helper to find the correct substep (copied substep for copy operations, original for move operations)
  const findTargetSubstep = (substepId) => {
    if (!substepId || !targetStep?.substeps) return null;
    
    if (isCopyOperation) {
      // For copy operations, we need to find the copied substep (with dummy tree_node_id)
      // If we have the tracked newCopiedSubstepTreeNodeId, use it directly
      if (newCopiedSubstepTreeNodeId) {
        const copiedSubstep = targetStep.substeps.find(substep =>
          substep.tree_node_id === newCopiedSubstepTreeNodeId
        );
        if (copiedSubstep) return copiedSubstep;
      }
      
      // Fallback: try to find the substep with dummy tree_node_id by matching substep_id_ref
      if (sourceSubstep) {
        const sourceSubstepIdRef = sourceSubstep.substep_id_ref;
        const substepWithDummyId = targetStep.substeps.find(substep =>
          substep.substep_id_ref === sourceSubstepIdRef &&
          substep.tree_node_id && substep.tree_node_id.startsWith('copy-')
        );
        if (substepWithDummyId) return substepWithDummyId;
      }
      
      // If not found by substep_id_ref, try matching by the substepId
      const matchingSubsteps = targetStep.substeps.filter(substep => {
        const matchesById = substep.tree_node_id === substepId || substep.substep_id_ref === substepId;
        return matchesById;
      }) || [];
      
      if (matchingSubsteps.length > 0) {
        // Prioritize the one with dummy tree_node_id (the copy)
        const copiedSubstep = matchingSubsteps.find(substep => 
          substep.tree_node_id && substep.tree_node_id.startsWith('copy-')
        );
        if (copiedSubstep) return copiedSubstep;
        
        // If multiple matches, return the one with highest sibling_order (likely the copy)
        if (matchingSubsteps.length > 1) {
          const sorted = [...matchingSubsteps].sort((a, b) => {
            const orderA = getSiblingOrderNumber(a);
            const orderB = getSiblingOrderNumber(b);
            return orderB - orderA; // Descending - get the one with highest order (the copy)
          });
          return sorted[0];
        }
        
        // If only one match, check if it's the copy (has dummy tree_node_id)
        const candidate = matchingSubsteps[0];
        if (candidate.tree_node_id && candidate.tree_node_id.startsWith('copy-')) {
          return candidate;
        }
        // Otherwise, it's the original substep - don't use it for copy operations
        return null;
      }
    }
    
    // For move operations or if not found, use standard lookup
    return matchSubstepById(targetStep.substeps, substepId) || findSubstepInStructure(clonedStructure, substepId);
  };

  const { type, parentSubstepId, id } = hierarchyNode;
  if (type === 'substep') {
    if (!Array.isArray(targetStep.substeps)) targetStep.substeps = [];
    
    // For copy operations, assign a dummy unique tree_node_id to track the new copied substep
    const strippedSubstep = stripUnaddedChildrenForSubstepCopy(hierarchyNode.node, 'substep');
    if (!strippedSubstep) return clonedStructure;
    
    if (isCopyOperation && strippedSubstep) {
      // Generate a dummy unique tree_node_id (using timestamp + random to ensure uniqueness)
      strippedSubstep.previous_tree_node_id = strippedSubstep.tree_node_id;
      strippedSubstep.tree_node_id = `copy-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      // Ensure the substep is empty (no children)
      delete strippedSubstep.children;
    }
    
    // Check if substep already exists to avoid duplicates
    const sourceSubstepId = sourceSubstep?.tree_node_id || sourceSubstep?.substep_id_ref;
    const existingSubstepIndex = targetStep.substeps.findIndex(substep => {
      if (isCopyOperation) {
        // For copy operations, check if this is the new copied substep (has dummy tree_node_id)
        return substep.tree_node_id && substep.tree_node_id.startsWith('copy-') &&
               (substep.substep_id_ref === sourceSubstepId || substep.substep_id_ref === (strippedSubstep?.substep_id_ref));
      }
      return substep.tree_node_id === sourceSubstepId || substep.substep_id_ref === sourceSubstepId;
    });
    
    if (existingSubstepIndex === -1) {
    const insertIndex = determineSubstepInsertIndex(targetStep.substeps, position, movePosition);
    targetStep.substeps.splice(insertIndex, 0, strippedSubstep);
    normalizeSubstepOrders(targetStep.substeps);
    } else {
      // Substep already exists, ensure it has no children (shouldn't happen in normal flow, but handle it)
      const existingSubstep = targetStep.substeps[existingSubstepIndex];
      delete existingSubstep.children;
    }
  } else if (type === 'substepChild') {
    if (!Array.isArray(targetStep.substeps)) targetStep.substeps = [];
    const parentSubstep = findTargetSubstep(parentSubstepId);
    if (parentSubstep) {
      if (!Array.isArray(parentSubstep.children)) parentSubstep.children = [];
      // Check if child already exists to avoid duplicates
      const childId = id || getChildNodeId(hierarchyNode.node);
      const existingChild = parentSubstep.children.find(child => {
        const existingChildId = getChildNodeId(child);
        return existingChildId === childId;
      });
      if (!existingChild) {
      const childClone = JSON.parse(JSON.stringify(hierarchyNode.node));
      // For copy operations, preserve the original tree_node_id as previous_tree_node_id
      if (isCopyOperation && childClone.tree_node_id) {
        childClone.previous_tree_node_id = childClone.tree_node_id;
      }
      parentSubstep.children.push(childClone);
      parentSubstep.children.forEach((child, index) => setSiblingOrder(child, index));
      }
    }
  }

  normalizeDocumentSiblingOrders(clonedStructure);
  return clonedStructure;
};

// Helper to extract document info
const getDocumentInfo = (doc, fallbackName = 'Document') => ({
  partNumber: doc?.partNumber || doc?.part_number || doc?.partId || fallbackName,
  revision: doc?.revision || doc?.revisionLabel || doc?.revision_id || '-',
});

// Helper to find step in sections
const findStepInSections = (sections, stepId) => {
  for (const section of sections) {
    const step = section.steps?.find(
      s => s.tree_node_id === stepId || s.step_id_ref === stepId
    );
    if (step) return { section, step };
  }
  return null;
};

// Helper to find substep in sections
const findSubstepInSections = (sections, substepId) => {
  for (const section of sections) {
    for (const step of section.steps || []) {
      const substep = step.substeps?.find(
        s => s.tree_node_id === substepId || s.substep_id_ref === substepId
      );
      if (substep) return { section, step, substep };
    }
  }
  return null;
};

// Helper to find final substep after copy/move
const findFinalSubstep = (newContent, sourceSubstep, position, movePosition, isCopyOperation = false) => {
  const sourceSubstepId = sourceSubstep.tree_node_id || sourceSubstep.substep_id_ref;
  
  // For copy operations with 'above' or 'below' position
  if (isCopyOperation && (position === 'above' || position === 'below') && movePosition?.targetId) {
    // First, find the target substep (the reference substep) to determine which step/section it's in
    const targetSubstepMatch = findSubstepInSections(newContent, movePosition.targetId);
    if (targetSubstepMatch) {
      const { section: targetSection, step: targetStep } = targetSubstepMatch;
      if (targetStep?.substeps) {
        // Find the copied substep in the same step as the target substep
        // The copied substep will have:
        // 1. A dummy tree_node_id starting with "copy-"
        // 2. The same substep_id_ref as the source substep
        const sourceSubstepIdRef = sourceSubstep.substep_id_ref;
        const copiedSubstep = targetStep.substeps.find(substep =>
          substep.substep_id_ref === sourceSubstepIdRef &&
          substep.tree_node_id && substep.tree_node_id.startsWith('copy-')
        );
        if (copiedSubstep) {
          return { section: targetSection, step: targetStep, substep: copiedSubstep };
        }
        
        // If not found by dummy ID, find by substep_id_ref and sibling_order
        const matchingSubsteps = targetStep.substeps.filter(substep =>
          substep.substep_id_ref === sourceSubstep.substep_id_ref
        );
        if (matchingSubsteps.length > 0) {
          // If multiple matches, the copy will have higher sibling_order (was added later)
          // If only one match, it's the copy (original is in a different step)
          const sorted = [...matchingSubsteps].sort((a, b) => {
            const orderA = getSiblingOrderNumber(a);
            const orderB = getSiblingOrderNumber(b);
            return orderB - orderA; // Descending - get the one with highest order (the copy)
          });
          return { section: targetSection, step: targetStep, substep: sorted[0] };
        }
      }
    }
  }
  
  // For copy operations with 'into' position
  if (isCopyOperation && position === 'into' && movePosition?.targetId) {
    // Find the target step first
    const targetStepMatch = findStepInSections(newContent, movePosition.targetId);
    if (targetStepMatch) {
      const { section: targetSection, step: targetStep } = targetStepMatch;
      if (targetStep?.substeps) {
        // Find the copied substep in the target step
        const sourceSubstepIdRef = sourceSubstep.substep_id_ref;
        const copiedSubstep = targetStep.substeps.find(substep =>
          substep.substep_id_ref === sourceSubstepIdRef &&
          substep.tree_node_id && substep.tree_node_id.startsWith('copy-')
        );
        if (copiedSubstep) {
          return { section: targetSection, step: targetStep, substep: copiedSubstep };
        }
        
        // If not found by dummy ID, find by substep_id_ref
        const matchingSubsteps = targetStep.substeps.filter(substep =>
          substep.substep_id_ref === sourceSubstep.substep_id_ref
        );
        if (matchingSubsteps.length > 0) {
          const sorted = [...matchingSubsteps].sort((a, b) => {
            const orderA = getSiblingOrderNumber(a);
            const orderB = getSiblingOrderNumber(b);
            return orderB - orderA;
          });
          return { section: targetSection, step: targetStep, substep: sorted[0] };
        }
      }
    }
  }
  
  // Fallback: Try to find by matching tree_node_id or substep_id_ref
  const match = findSubstepInSections(newContent, sourceSubstepId);
  if (match) return match;
  
  return null;
};

// Helper to collect all possible IDs for a node
const collectPossibleIds = (id, node) => {
  const possibleIds = [id].filter(Boolean);
  if (!node) return possibleIds;
  
  const idFields = ['tree_node_id', 'step_id_ref', 'substep_id_ref', 'node_id'];
  idFields.forEach(field => {
    if (node[field] && node[field] !== id) possibleIds.push(node[field]);
  });
  
  if (node.detail) {
    CHILD_NODE_ID_FIELDS.forEach(field => {
      if (node.detail[field]) possibleIds.push(node.detail[field]);
    });
  }
  
  return possibleIds;
};

// Helper to resolve original name
const resolveOriginalName = (item, originalIndexMap, finalIndexMap, childTypeCounters) => {
  const { id, node, type } = item;
  
  const defaultLabel = type === 'substep' ? 'Substep' : getChildTypeDisplayName(node?.type);

  const possibleIds = collectPossibleIds(id, node);
  
  // For child nodes, use counter-based naming
  if (type === 'substepChild') {
    const normalizedChildType = normalizeChildType(node?.type || '');
    if (normalizedChildType && childTypeCounters.hasOwnProperty(normalizedChildType)) {
      childTypeCounters[normalizedChildType] += 1;
      const childDisplayName = getChildTypeDisplayName(normalizedChildType);
      const mappedId = possibleIds.find(candidateId => candidateId && (originalIndexMap[candidateId] || finalIndexMap[candidateId]));
      if (mappedId) {
        return originalIndexMap[mappedId] || finalIndexMap[mappedId];
      }
      return `${childDisplayName} ${childTypeCounters[normalizedChildType]}`;
    }
  }
  
  // Try to find in index maps
  const mappedId = possibleIds.find(candidateId => candidateId && (originalIndexMap[candidateId] || finalIndexMap[candidateId]));
  if (mappedId) {
    return originalIndexMap[mappedId] || finalIndexMap[mappedId];
  }
  
  return resolveNodeNameWithFallback(id, originalIndexMap, node, defaultLabel);
};

// Shared logic for building node indexing
const buildNodeIndexingCore = ({
  sourceSubstep,
  sourceAllNodes,
  newContent,
  targetDoc,
  sourceDoc,
  position,
  movePosition,
  actionType, // 'Move' or 'Copy'
  buildActionPrefix,
  targetAllNodesOriginal, // Original target document nodes (before copy/move)
}) => {
  if (!sourceSubstep || !Array.isArray(newContent) || newContent.length === 0) {
    return null;
  }

  // Normalize sibling orders in newContent to ensure correct indexing
  const normalizedNewContent = structuredClone(newContent);
  normalizeDocumentSiblingOrders(normalizedNewContent);
  
  const originalIndexMap = generateNodeIndexing(sourceAllNodes || []);
  const finalIndexMap = generateNodeIndexing(normalizedNewContent);
  
  // For target substep name, use original target document (before copy/move) to get correct name
  const targetSnapshot = Array.isArray(targetAllNodesOriginal) ? targetAllNodesOriginal : [];
  const targetIndexMap = targetSnapshot.length
    ? generateNodeIndexing(targetSnapshot)
    : finalIndexMap;
  
  const isCopyOperation = actionType === 'Copy';
  const isMoveOperation = actionType === 'Move';
  const finalMatch = findFinalSubstep(normalizedNewContent, sourceSubstep, position, movePosition, isCopyOperation);
  if (!finalMatch) return null;
  
  const { section: finalSection, step: finalStep, substep: finalSubstep } = finalMatch;

  const finalSectionName = finalIndexMap[finalSection.tree_node_id] ||
    resolveNodeNameWithFallback(finalSection.tree_node_id, originalIndexMap, finalSection, 'Section');
    
  const finalStepName = finalIndexMap[finalStep.tree_node_id] ||
    resolveNodeNameWithFallback(finalStep.tree_node_id, originalIndexMap, finalStep, 'Step');
    
  const finalSubstepName = finalIndexMap[finalSubstep.tree_node_id] ||
    finalIndexMap[finalSubstep.substep_id_ref] ||
    resolveNodeNameWithFallback(
      finalSubstep.tree_node_id || finalSubstep.substep_id_ref,
      originalIndexMap,
      finalSubstep,
      'Substep'
    );

  let targetSubstepFinalName = '';
  if (position !== 'into' && movePosition?.targetId) {
    // For "above" or "below", get target substep name from ORIGINAL target document (before copy/move)
    // This ensures we get the correct name (e.g., "Substep 1A") before it gets shifted
    const targetMatch = targetAllNodesOriginal 
      ? findSubstepInSections(targetAllNodesOriginal, movePosition.targetId)
      : findSubstepInSections(normalizedNewContent, movePosition.targetId);
    if (targetMatch) {
      targetSubstepFinalName = targetIndexMap[targetMatch.substep.tree_node_id] ||
        targetIndexMap[targetMatch.substep.substep_id_ref] ||
        resolveNodeNameWithFallback(
          targetMatch.substep.tree_node_id || targetMatch.substep.substep_id_ref,
          originalIndexMap,
          targetMatch.substep,
          'Substep'
        );
    }
  }

  const originalSubstepName = resolveNodeNameWithFallback(
    sourceSubstep.tree_node_id || sourceSubstep.substep_id_ref,
    originalIndexMap,
    sourceSubstep,
    'Substep'
  );

  const targetInfo = getDocumentInfo(targetDoc, 'Target Document');
  const sourceInfo = sourceDoc ? getDocumentInfo(sourceDoc, 'Source Document') : null;
  
  const sectionDescriptor = finalSectionName || 'the target section';
  const stepDescriptor = finalStepName || 'the target step';
  const referenceSubstepName = targetSubstepFinalName || finalSubstepName || 'the target substep';
  const destinationBase = `${targetInfo.partNumber}, Rev ${targetInfo.revision}`;
  
  const actionPrefix = buildActionPrefix(
    originalSubstepName,
    position,
    sectionDescriptor,
    stepDescriptor,
    referenceSubstepName,
    destinationBase,
    sourceInfo,
    targetInfo
  );

  const hierarchyNodes = collectSubstepHierarchyNodes(sourceSubstep);
  if (hierarchyNodes.length === 0) return null;

  const childTypeCounters = { attachment: 0, canvas: 0, 'rich-text': 0, video: 0, form: 0 };
  // Filter out nodes without IDs to get accurate total operations count
  const validNodes = hierarchyNodes.filter(item => item.id);
  const totalOperations = validNodes.length;
  const nodeIndexing = {};
  const baseStructure = cloneStructureForIndexing(targetSnapshot);
  const initialStructure = isMoveOperation
    ? setBlakSubstep(baseStructure, sourceSubstep)
    : baseStructure;
  let currentStructure = isMoveOperation ? JSON.parse(JSON.stringify(initialStructure)).map(item => {
    if(item.steps){
      item.steps = item.steps.map(step => {
        if(step.substeps){
          step.substeps = step.substeps.filter(substep => substep.version_id_ref !== sourceSubstep.version_id_ref)
        }
        return step
      })
    }
    return item
  }) : JSON.parse(JSON.stringify(initialStructure));

  // Track the new copied substep's tree_node_id for same-document copies
  let newCopiedSubstepTreeNodeId = null;
  
  // Track the previous newContent state for copy operations
  // originalContent for each operation should be the newContent from the previous operation
  let previousNewContent = null;

  // Use a separate counter for operation numbers that only increments when we create an entry
  let operationCounter = 0;
  const elementTitles = generateTitlesFromStructure(sourceAllNodes);
  hierarchyNodes.forEach((item, index) => {
    const { id, node, type } = item;
    if (!id) return;

    operationCounter++; // Increment only when we process a valid node

    const originalName = elementTitles[node.tree_node_id];

    const destinationName = formatSubstepDestinationName({
      type,
      finalStepName,
      finalSubstepName,
    });

    const movementPhrase = actionType === 'Copy' 
      ? `in ${destinationName} in ${destinationBase}`
      : `in ${destinationName}`;

    const title = `${actionPrefix} System Performs Operation ${operationCounter} of ${totalOperations}, ${actionType} ${originalName} ${movementPhrase}.`;

    // For copy operations, originalContent should be the previous newContent (state after previous operation)
    // For the first operation, use currentStructure (state before adding the substep)
    let originalContent = null;
    if (isCopyOperation) {
      if(previousNewContent){
        originalContent = JSON.parse(JSON.stringify(previousNewContent));
      }else {
        originalContent = structuredClone(currentStructure || []);
      }
    }
    
    // Add the node to structure
    currentStructure = addNodeToSubstepStructure({
      currentStructure,
      hierarchyNode: item,
      finalSection,
      finalStep,
      finalSubstep,
      position,
      movePosition,
      isCopyOperation,
      sourceSubstep,
      newCopiedSubstepTreeNodeId,
    });
    
    // Track the new copied substep's tree_node_id when we add it in the first operation
    // Do this AFTER adding the substep so we can use it for subsequent operations
    if (isCopyOperation && type === 'substep') {
      // Find the newly added substep in currentStructure by looking for the dummy tree_node_id
      const sourceSubstepIdRef = sourceSubstep.substep_id_ref;
      const sectionId = finalSection?.tree_node_id || finalSection?.section_id_ref;
      const stepId = finalStep?.tree_node_id || finalStep?.step_id_ref;
      const targetSection = currentStructure.find(sec => 
        (sec.tree_node_id === sectionId || sec.section_id_ref === sectionId)
      );
      if (targetSection) {
        const targetStep = targetSection.steps?.find(step =>
          (step.tree_node_id === stepId || step.step_id_ref === stepId)
        );
        if (targetStep?.substeps) {
          const addedSubstep = targetStep.substeps.find(substep => 
            substep.substep_id_ref === sourceSubstepIdRef &&
            substep.tree_node_id && substep.tree_node_id.startsWith('copy-')
          );
          if (addedSubstep && addedSubstep.tree_node_id) {
            newCopiedSubstepTreeNodeId = addedSubstep.tree_node_id;
          }
        }
      }
    }
    
    const newContentState = structuredClone(currentStructure || []);

    if(isMoveOperation){
      if(index === 0) {
        originalContent = structuredClone(initialStructure);
      }else{
        originalContent = JSON.parse(JSON.stringify(previousNewContent)).map(item => {
          if(item.steps){
            item.steps = item.steps.map(step => {
              if(step.substeps){
                step.substeps = step.substeps.map(substep => {
                  if(substep.version_id_ref === sourceSubstep.version_id_ref) {
                    newContentState.forEach(item => {
                      if(item.steps){
                        item.steps.forEach(newstep => {
                          if(newstep.substeps){
                            newstep.substeps.forEach(newsubstep => {
                              if(newsubstep.version_id_ref === sourceSubstep.version_id_ref) {
                                substep = newsubstep;
                              }
                            })
                          }
                        })
                      }
                    })
                  }
                  return substep;
                })
              }
              return step;
            })
          }
          return item;
        })
      }
    } 
    
    nodeIndexing[id] = {
      title,
      originalContent: originalContent || [],
      newContent: newContentState || [],
    };
    
    // Update previousNewContent for the next operation (for copy operations)
    // Store the newContentState so it can be used as originalContent for the next operation
    if(isCopyOperation) {
      previousNewContent = JSON.parse(JSON.stringify(newContentState || []));
    }else{
      previousNewContent = JSON.parse(JSON.stringify(originalContent || []));
    }
    
  });

  return Object.keys(nodeIndexing).length ? nodeIndexing : null;
};

const buildNodeIndexingForSubstepMove = ({
  sourceSubstep,
  sourceAllNodes,
  newContent,
  targetDoc,
  position,
  movePosition,
  targetAllNodesOriginal,
}) => {
  // Find source section and source step that contains the source substep
  const originalIndexMap = generateNodeIndexing(sourceAllNodes || []);
  let sourceSection = null;
  let sourceStep = null;
  for (const section of sourceAllNodes || []) {
    for (const step of section.steps || []) {
      const substep = (step.substeps || []).find(
        s => s.tree_node_id === sourceSubstep.tree_node_id || s.substep_id_ref === sourceSubstep.substep_id_ref
      );
      if (substep) {
        sourceSection = section;
        sourceStep = step;
        break;
      }
    }
    if (sourceStep) break;
  }
  
  const sourceSectionName = sourceSection 
    ? (originalIndexMap[sourceSection.tree_node_id] || 
       originalIndexMap[sourceSection.section_id_ref] || 
       `Section ${((sourceAllNodes || []).findIndex(s => s.tree_node_id === sourceSection.tree_node_id) + 1)}`)
    : 'the source section';

  const sourceStepName = sourceStep 
    ? (originalIndexMap[sourceStep.tree_node_id] || 
       originalIndexMap[sourceStep.step_id_ref] || 
       `Step ${calculateGlobalStepNumber(sourceAllNodes || [], sourceStep)}`)
    : 'the source step';
  
  return buildNodeIndexingCore({
    sourceSubstep,
    sourceAllNodes,
    newContent,
    targetDoc,
    sourceDoc: null,
    position,
    movePosition,
    actionType: 'Move',
    targetAllNodesOriginal: targetAllNodesOriginal || sourceAllNodes,
    buildActionPrefix: (originalSubstepName, position, sectionDescriptor, stepDescriptor, referenceSubstepName, destinationBase) => {
      const positionDescriptor = position === 'into'
        ? `into ${stepDescriptor} of ${sectionDescriptor}`
        : position === 'above'
        ? `above ${referenceSubstepName} in ${stepDescriptor} of ${sectionDescriptor}`
        : `below ${referenceSubstepName} in ${stepDescriptor} of ${sectionDescriptor}`;
      return `User Moved ${originalSubstepName} from ${sourceStepName} of ${sourceSectionName} ${positionDescriptor} in ${destinationBase}.`;
    },
  });
};

export const buildNodeIndexingForSubstepCopy = ({
  sourceSubstep,
  sourceAllNodes,
  newContent,
  targetDoc,
  sourceDoc,
  position,
  movePosition,
  targetAllNodesOriginal,
}) => {
  // Find source section and source step that contains the source substep
  const originalIndexMap = generateNodeIndexing(sourceAllNodes || []);
  let sourceSection = null;
  let sourceStep = null;
  for (const section of sourceAllNodes || []) {
    for (const step of section.steps || []) {
      const substep = (step.substeps || []).find(
        s => s.tree_node_id === sourceSubstep.tree_node_id || s.substep_id_ref === sourceSubstep.substep_id_ref
      );
      if (substep) {
        sourceSection = section;
        sourceStep = step;
        break;
      }
    }
    if (sourceStep) break;
  }
  
  const sourceSectionName = sourceSection 
    ? (originalIndexMap[sourceSection.tree_node_id] || 
       originalIndexMap[sourceSection.section_id_ref] || 
       `Section ${((sourceAllNodes || []).findIndex(s => s.tree_node_id === sourceSection.tree_node_id) + 1)}`)
    : 'the source section';

  const sourceStepName = sourceStep 
    ? (originalIndexMap[sourceStep.tree_node_id] || 
       originalIndexMap[sourceStep.step_id_ref] || 
       `Step ${calculateGlobalStepNumber(sourceAllNodes || [], sourceStep)}`)
    : 'the source step';
  
  return buildNodeIndexingCore({
    sourceSubstep,
    sourceAllNodes,
    newContent,
    targetDoc,
    sourceDoc,
    position,
    movePosition,
    actionType: 'Copy',
    targetAllNodesOriginal,
    buildActionPrefix: (originalSubstepName, position, sectionDescriptor, stepDescriptor, referenceSubstepName, destinationBase, sourceInfo) => {
      const sourceDocInfo = sourceInfo ? `in ${sourceInfo.partNumber}, Rev ${sourceInfo.revision}` : '';
      
      const positionDescriptor = position === 'into'
        ? `into ${stepDescriptor} of ${sectionDescriptor} in ${destinationBase}`
        : position === 'above'
        ? `above ${referenceSubstepName} in ${stepDescriptor} of ${sectionDescriptor} in ${destinationBase}`
        : `below ${referenceSubstepName} in ${stepDescriptor} of ${sectionDescriptor} in ${destinationBase}`;
      
      return `User Copied ${originalSubstepName} from ${sourceStepName} of ${sourceSectionName} ${sourceDocInfo}, ${positionDescriptor}.`;
    },
  });
};

/**
 * Calculate global step number for a step across all sections
 * @param {Array} allNodes - All nodes from the document
 * @param {Object} step - The step object (or substep with step_id_ref)
 * @returns {number} - Global step number (1-based)
 */
const calculateGlobalStepNumber = (allNodes, stepOrSubstep) => {
  if (!allNodes || !stepOrSubstep) return 1;
  
  // Get step_id_ref from step or substep
  const stepIdRef = stepOrSubstep.step_id_ref;
  if (!stepIdRef) return 1;
  
  // Sort sections by sibling_order
  const sortedSections = [...allNodes].sort((a, b) => {
    const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
    const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
    return orderA - orderB;
  });
  
  // Find the section and step
  for (let i = 0; i < sortedSections.length; i++) {
    const section = sortedSections[i];
    const steps = section.steps || [];
    const sortedSteps = [...steps].sort((a, b) => {
      const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
      const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
      return orderA - orderB;
    });
    
    const stepIndex = sortedSteps.findIndex(
      s => s.step_id_ref === stepIdRef || s.tree_node_id === stepIdRef
    );
    
    if (stepIndex !== -1) {
      // Count steps in all sections before this one
      const stepsBeforeThisSection = sortedSections
        .slice(0, i)
        .reduce((total, s) => total + (s.steps?.length || 0), 0);
      
      return stepsBeforeThisSection + stepIndex + 1;
    }
  }
  
  return 1;
};

// Helper to build action_to_show for substep operations
const buildSubstepActionToShow = (sourceSubstep, targetAllNodes, targetDoc, position, movePosition, isCopy = false) => {
  const sourceGlobalStepNumber = calculateGlobalStepNumber(targetAllNodes, sourceSubstep);
  const substepLetter = getSubstepLetter(targetAllNodes, sourceSubstep);
  const sourceSubstepIdentifier = `${sourceGlobalStepNumber}${substepLetter}`;
  
  // Find source section index
  let sourceSectionIndex = 0;
  const sortedSections = [...targetAllNodes].sort((a, b) => {
    const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
    const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
    return orderA - orderB;
  });
  for (let i = 0; i < sortedSections.length; i++) {
    const section = sortedSections[i];
    if (section.section_id_ref === sourceSubstep.section_id_ref) {
      sourceSectionIndex = i;
      break;
    }
  }
  
  const action = isCopy ? 'Copied' : 'Moved';
  const docInfo = `in ${targetDoc.partNumber}, Rev ${targetDoc.revision}`;
  
  if (position === 'into') {
    const sectionIndex = movePosition.sectionIndex ?? movePosition.targetSectionIndex ?? movePosition.targetIndex;
    const targetStepNumber = movePosition.globalStepNumber ?? (movePosition.targetIndex !== undefined ? movePosition.targetIndex + 1 : '');
    return `${action} Substep ${sourceSubstepIdentifier} into Section ${sectionIndex !== undefined ? sectionIndex + 1 : ''} > Step ${targetStepNumber} ${docInfo}`;
  }
  
  const positionText = position === 'above' ? 'above' : 'below';
  const sectionIndex = movePosition.targetSectionIndex ?? movePosition.sectionIndex;
  const targetStepNumber = movePosition.globalStepNumber ?? (movePosition.targetStepIndex !== undefined ? movePosition.targetStepIndex + 1 : '');
  
  // Find target substep identifier
  let targetSubstepIdentifier = movePosition.targetIndex !== undefined ? movePosition.targetIndex + 1 : '';
  if (movePosition.targetId) {
    const targetMatch = findSubstepInSections(targetAllNodes, movePosition.targetId);
    if (targetMatch) {
      const targetGlobalStepNumber = calculateGlobalStepNumber(targetAllNodes, targetMatch.substep);
      const targetSubstepLetter = getSubstepLetter(targetAllNodes, targetMatch.substep);
      targetSubstepIdentifier = `${targetGlobalStepNumber}${targetSubstepLetter}`;
    }
  }
  
  return `${action} Substep ${sourceSubstepIdentifier} from Section ${sourceSectionIndex + 1} > Step ${sourceGlobalStepNumber} into Section ${sectionIndex !== undefined ? sectionIndex + 1 : ''} > Step ${targetStepNumber} ${positionText} Substep ${targetSubstepIdentifier} ${docInfo}`;
};

/**
 * Calculate move/copy parameters for substeps based on position
 * @param {Object} params - Parameters object
 * @param {string} params.position - 'into' | 'above' | 'below'
 * @param {Object} params.targetAllNodes - All nodes from target document
 * @param {Object} params.targetDoc - Target document info
 * @param {Object} params.movePosition - Position details with targetId and targetType
 * @param {boolean} params.moveCreateCopy - Whether this is a copy operation
 * @param {Object} params.sourceSubstep - Source substep being moved
 * @param {Object} params.moveDocument - Source document info (to check if same document)
 * @returns {Object} - { toOrder, toProcedureTreeNodeRef, toSectionIdRef, toStepIdRef, toSubstepIdRef, direction, direction_move_into }
 */
export const calculateSubstepMoveParameters = ({
  position,
  targetAllNodes,
  targetDoc,
  movePosition,
  moveCreateCopy,
  sourceSubstep,
  moveDocument,
}) => {
  let toOrder, toProcedureTreeNodeRef, toSectionIdRef, toStepIdRef, toSubstepIdRef, direction, direction_move_into;
  
  let targetSection = null;
  let targetStep = null;
  let targetSubstep = null;

  if (position === 'into') {
    // For "into", target is a step (add substep to end of step's substeps)
    // Find the target step in all sections
    for (const section of targetAllNodes) {
      targetStep = section.steps?.find(
        step => step.tree_node_id === movePosition.targetId || 
                step.step_id_ref === movePosition.targetId
      );
      if (targetStep) {
        targetSection = section;
        break;
      }
    }

    if (!targetStep || !targetSection) {
      throw new Error('Target step not found.');
    }

    // Get all substeps in target step
    const targetSubsteps = targetStep.substeps || [];
    
    // Calculate max order and add 1
    const allOrders = targetSubsteps
      .map((substep) =>
        Number(substep.sibling_order || substep.procedure_tree?.sibling_order || 0)
      )
      .filter((order) => !isNaN(order));

    toOrder = allOrders.length > 0 ? Math.max(...allOrders) + 1 : 1;
    
    // For "into" step: to_procedure_tree_node_ref = last substep's tree_node_id (or step's tree_node_id if no substeps)
    if (targetSubsteps.length > 0) {
      // Sort substeps by order and get the last one
      const sortedSubsteps = [...targetSubsteps].sort((a, b) => {
        const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
        const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
        return orderA - orderB;
      });
      toProcedureTreeNodeRef = sortedSubsteps[sortedSubsteps.length - 1].tree_node_id;
    } else {
      // No substeps in target step, use step's tree_node_id
      toProcedureTreeNodeRef = targetStep.tree_node_id;
    }
    
    toSectionIdRef = targetSection.section_id_ref;
    toStepIdRef = targetStep.step_id_ref;
    toSubstepIdRef = null; // No target substep for "into"
    direction = moveCreateCopy ? 'paste_down' : 'move_down';
    direction_move_into = moveCreateCopy ? true : true; // Both move and copy use true for "into"
  } else {
    // For "above" or "below", target is a substep
    // First, find the substep in all sections and steps
    for (const section of targetAllNodes) {
      for (const step of section.steps || []) {
        targetSubstep = step.substeps?.find(
          substep => substep.tree_node_id === movePosition.targetId || 
                     substep.substep_id_ref === movePosition.targetId
        );
        if (targetSubstep) {
          targetStep = step;
          targetSection = section;
          break;
        }
      }
      if (targetSubstep) break;
    }

    if (!targetSubstep || !targetStep || !targetSection) {
      throw new Error('Target substep not found.');
    }

    const targetOrder = parseInt(targetSubstep.sibling_order || targetSubstep.procedure_tree?.sibling_order || '0', 10);
    const sourceOrder = sourceSubstep ? parseInt(sourceSubstep.sibling_order || sourceSubstep.procedure_tree?.sibling_order || '0', 10) : null;
    
    // Check if this is a same-document, same-step move (not copy)
    const isSameDocumentMove = !moveCreateCopy && 
                               sourceSubstep && 
                               moveDocument && 
                               moveDocument.partId === targetDoc.partId && 
                               moveDocument.revisionId === targetDoc.revisionId;
    
    const isSameStep = sourceSubstep?.step_id_ref === targetStep.step_id_ref;

    // For move operations in the same step, we need to account for the source substep being removed
    if (isSameDocumentMove && isSameStep && sourceOrder !== null) {
      // Same-step move: adjust order accounting for source removal
      if (sourceOrder < targetOrder) {
        // Source comes before target, target shifts down
        if (position === 'above') {
          toOrder = targetOrder - 1;
        } else {
          toOrder = targetOrder;
        }
      } else {
        // Source comes after target, target order doesn't change
        if (position === 'above') {
          toOrder = targetOrder;
        } else {
          toOrder = targetOrder + 1;
        }
      }
    } else {
      // For copy operations or cross-step moves
      if (position === 'above') {
        toOrder = targetOrder;
      } else {
        toOrder = targetOrder + 1;
      }
    }

    direction = moveCreateCopy ? (position === 'above' ? 'paste_up' : 'paste_down') : (position === 'above' ? 'move_up' : 'move_down');

    // For ABOVE/BELOW:
    // Ensure targetSubstep has tree_node_id
    if (!targetSubstep.tree_node_id) {
      throw new Error('Target substep missing tree_node_id');
    }
    toProcedureTreeNodeRef = targetSubstep.tree_node_id; // Use substep's tree_node_id
    toSectionIdRef = targetSection.section_id_ref;
    toStepIdRef = targetStep.step_id_ref;
    toSubstepIdRef = targetSubstep.substep_id_ref;
    
    // direction_move_into logic:
    // - Same step: false (for moves), true (for copies)
    // - Cross step: true (for both moves and copies) - CRITICAL from payload analysis
    if (isSameStep) {
      direction_move_into = moveCreateCopy ? true : false; // Copies always true, moves false for same-step
    } else {
      // Cross-step: CRITICAL - always set to true (for both moves and copies, as per payloads)
      direction_move_into = true;
    }
  }

  return {
    toOrder,
    toProcedureTreeNodeRef,
    toSectionIdRef,
    toStepIdRef,
    toSubstepIdRef,
    direction,
    direction_move_into,
  };
};

/**
 * Prepare new content structure for substep move/copy preview
 * @param {Object} params - Parameters object
 * @param {Array} params.targetAllNodes - All nodes from target document
 * @param {Object} params.sourceSubstep - Source substep being moved/copied
 * @param {Object} params.moveDocument - Source document info
 * @param {Object} params.targetDoc - Target document info
 * @param {string} params.position - 'into' | 'above' | 'below'
 * @param {Object} params.movePosition - Position details
 * @param {string} params.toStepIdRef - Target step ID reference
 * @param {boolean} params.moveCreateCopy - Whether this is a copy operation
 * @returns {Array} - New content structure with substep moved/copied
 */
export const prepareSubstepMoveNewContent = ({
  targetAllNodes,
  sourceSubstep,
  moveDocument,
  targetDoc,
  position,
  movePosition,
  toStepIdRef,
  moveCreateCopy,
}) => {
  const newContent = structuredClone(targetAllNodes);
  const sourceStepIdRef = sourceSubstep?.step_id_ref;

  // Deep clone the source substep
  const clonedSubstep = structuredClone(sourceSubstep);

  if (position === 'into') {
    // Find target step and add substep to end
    let targetStep = null;
    let targetSection = null;

    for (const section of newContent) {
      targetStep = section.steps?.find(
        step => step.tree_node_id === movePosition.targetId || 
                step.step_id_ref === movePosition.targetId
      );
      if (targetStep) {
        targetSection = section;
        break;
      }
    }

    if (targetStep) {
      if (moveCreateCopy) {
        // Copy: just add to target step
        targetStep.substeps = [...(targetStep.substeps || []), clonedSubstep];
      } else {
        // Move: remove from source, add to target
        const isSameDocument = moveDocument.partId === targetDoc.partId && 
                               moveDocument.revisionId === targetDoc.revisionId;
        
        if (isSameDocument && sourceStepIdRef) {
          // Remove from source step
          let sourceStep = null;
          let sourceSection = null;
          
          for (const section of newContent) {
            sourceStep = section.steps?.find(step => step.step_id_ref === sourceStepIdRef);
            if (sourceStep) {
              sourceSection = section;
              break;
            }
          }
          
          if (sourceStep && sourceStep.substeps) {
            sourceStep.substeps = sourceStep.substeps.filter(
              s => s.substep_id_ref !== sourceSubstep.substep_id_ref && 
                   s.tree_node_id !== sourceSubstep.tree_node_id
            );
          }
        }
        // Add to target step
        targetStep.substeps = [...(targetStep.substeps || []), clonedSubstep];
      }
    }
  } else {
    // For "above" or "below", find target substep and insert
    let targetSubstep = null;
    let targetStep = null;
    let targetSection = null;

    for (const section of newContent) {
      for (const step of section.steps || []) {
        targetSubstep = step.substeps?.find(
          substep => substep.tree_node_id === movePosition.targetId || 
                     substep.substep_id_ref === movePosition.targetId
        );
        if (targetSubstep) {
          targetStep = step;
          targetSection = section;
          break;
        }
      }
      if (targetSubstep) break;
    }

    if (targetSubstep && targetStep && targetStep.substeps) {
      const targetIndex = targetStep.substeps.findIndex(
        substep => substep.tree_node_id === movePosition.targetId || 
                   substep.substep_id_ref === movePosition.targetId
      );

      if (targetIndex !== -1) {
        const isSameDocument = moveDocument.partId === targetDoc.partId && 
                               moveDocument.revisionId === targetDoc.revisionId;
        const isSameStep = sourceStepIdRef === targetStep.step_id_ref;

        if (moveCreateCopy) {
          // Copy: just insert, don't remove
          if (position === 'above') {
            targetStep.substeps.splice(targetIndex, 0, clonedSubstep);
          } else {
            targetStep.substeps.splice(targetIndex + 1, 0, clonedSubstep);
          }
        } else {
          // Move: remove from source, insert at target
          if (isSameDocument && sourceStepIdRef) {
            // Remove from source step first
            let sourceStep = null;
            
            for (const section of newContent) {
              sourceStep = section.steps?.find(step => step.step_id_ref === sourceStepIdRef);
              if (sourceStep) break;
            }
            
            if (sourceStep && sourceStep.substeps) {
              const sourceIndex = sourceStep.substeps.findIndex(
                s => s.substep_id_ref === sourceSubstep.substep_id_ref && 
                     s.tree_node_id === sourceSubstep.tree_node_id
              );
              
              if (sourceIndex !== -1) {
                sourceStep.substeps.splice(sourceIndex, 1);
                
                // Adjust target index if removing from same step before target
                if (isSameStep && sourceIndex < targetIndex) {
                  // Target shifted down by 1
                  if (position === 'above') {
                    targetStep.substeps.splice(targetIndex - 1, 0, clonedSubstep);
                  } else {
                    targetStep.substeps.splice(targetIndex, 0, clonedSubstep);
                  }
                } else {
                  // Different step or source after target
                  if (position === 'above') {
                    targetStep.substeps.splice(targetIndex, 0, clonedSubstep);
                  } else {
                    targetStep.substeps.splice(targetIndex + 1, 0, clonedSubstep);
                  }
                }
              } else {
                // Source not found, just insert
                if (position === 'above') {
                  targetStep.substeps.splice(targetIndex, 0, clonedSubstep);
                } else {
                  targetStep.substeps.splice(targetIndex + 1, 0, clonedSubstep);
                }
              }
            }
          } else {
            // Cross-document move: just insert
            if (position === 'above') {
              targetStep.substeps.splice(targetIndex, 0, clonedSubstep);
            } else {
              targetStep.substeps.splice(targetIndex + 1, 0, clonedSubstep);
            }
          }
        }
      }
    }
  }

  return newContent;
};

/**
 * Build version content for copy operations
 * Note: Same structure as move, but no rule_name or modified_fields
 */
export const buildSubstepCopyVersionContent = ({
  action_to_show,
  allRevisionNodes,
  newContent,
  moveType,
  revision,
  status,
  type,
  partNumber,
  targetSectionIndex,
  targetStepIndex,
  item_code_type
}) => {
  // Map "above" to "up" to match API expectations
  const mappedMoveType = moveType === 'above' ? 'up' : moveType;
  
  // Format: "Section 3 > Step 9 in TARGETREV-19, Rev A-"
  const stepIndex = targetStepIndex !== undefined ? targetStepIndex + 1 : '';
  
  return {
    action_to_show,
    moveType: mappedMoveType,
    rev: revision,
    status,
    type,
    item_code_type: item_code_type,
    partNumber,
    move_copy_action: `Section ${targetSectionIndex + 1} > Step ${stepIndex} in ${partNumber}, Rev ${revision}`,
    main_entry: true,
  };
};

/**
 * Build version content for move operations
 */
export const buildSubstepMoveVersionContent = async ({
  action_to_show,
  allRevisionNodes,
  newContent,
  moveType,
  revision,
  status,
  type,
  item_code_type,
  partNumber,
  partDescription,
  targetSectionIndex,
  targetStepIndex,
  parentVersion,
}) => {
  // Map "above" to "up" to match API expectations
  const mappedMoveType = moveType === 'above' ? 'up' : moveType;
  
  // Format: "Section 3 > Step 9 in TARGETREV-19, Rev A-"
  const stepIndex = targetStepIndex !== undefined ? targetStepIndex + 1 : '';
  return {
    action_to_show,
    moveType: mappedMoveType,
    rev: revision,
    status,
    type,
    item_code_type: item_code_type,
    partNumber,
    partDescription,
    move_copy_action: `Section ${targetSectionIndex + 1} > Step ${stepIndex} in ${partNumber}, Rev ${revision}`,
    main_entry: true,
  };
};

// Consolidated cleanup function
const cleanupPayload = (payload) => {
  const cleaned = { ...payload };
  Object.keys(cleaned).forEach(key => {
    if (cleaned[key] === undefined || cleaned[key] === null) {
      delete cleaned[key];
    }
  });
  return cleaned;
};

export const cleanupSubstepCopyPayload = cleanupPayload;
export const cleanupSubstepMovePayload = cleanupPayload;

/**
 * Build copy payload for substep
 */
export const buildSubstepCopyPayload = async ({
  sourceSubstep,
  moveDocument,
  targetDoc,
  position,
  movePosition,
  toOrder,
  toProcedureTreeNodeRef,
  toSectionIdRef,
  toStepIdRef,
  toSubstepIdRef,
  direction,
  direction_move_into,
  moveComment,
  targetAllNodesOriginal,
  sourceAllNodes = null,
  newContent,
  item_code_type
}) => {

  // Build version content
  const versionContent = buildSubstepCopyVersionContent({
    action_to_show:"",
    allRevisionNodes: targetAllNodesOriginal,
    newContent,
    moveType: position,
    revision: targetDoc.revision,
    status: targetDoc.status || 'Not Submitted',
    item_code_type: item_code_type,
    partNumber: targetDoc.partNumber,
    targetSectionIndex: movePosition.targetSectionIndex !== undefined ? movePosition.targetSectionIndex : (movePosition.sectionIndex !== undefined ? movePosition.sectionIndex : 0),
    targetStepIndex: movePosition.targetIndex !== undefined ? movePosition.targetIndex : undefined,
  });

  // Build audit trail data
  // Note: Based on payload analysis, COPY operations use action "SUBSTEP_MOVED" (not "SUBSTEP_COPIED")
  // and do NOT include parent_version in audit_trail_data
  // hash should be empty string
  const audit_trail_data = await getAuditTrailData(
    {
      action: 'SUBSTEP_COPIED', // COPY operations also use SUBSTEP_MOVED (per payload analysis)
      comment: moveComment,
      parent_version: null, // COPY operations do NOT include parent_version (per payload analysis)
      version_content: versionContent,
    },
    false
  );
  
  // Override hash to empty string for copy operations
  audit_trail_data.hash = '';

  // Validate toProcedureTreeNodeRef for above/below operations
  if (position !== 'into' && !toProcedureTreeNodeRef) {
    throw new Error('to_procedure_tree_node_ref is required for above/below operations');
  }

  // Generate node_indexing for substep copy operation
  const nodeIndexing = buildNodeIndexingForSubstepCopy({
    sourceSubstep,
    sourceAllNodes: sourceAllNodes || [],
    newContent: newContent || [],
    targetDoc,
    sourceDoc: moveDocument,
    position,
    movePosition,
    targetAllNodesOriginal: targetAllNodesOriginal || [],
  });

  audit_trail_data.version_content.action_to_show = Object.values(nodeIndexing)[0]?.title?.split("System Performs Operation")[0].trim();

  // Build base payload
  const payload = {
    from_procedure_tree_node_ref: sourceSubstep.tree_node_id,
    from_order: parseInt(sourceSubstep.sibling_order || sourceSubstep.procedure_tree?.sibling_order || '1', 10),
    from_type: 'substep',
    from_part_id_ref: moveDocument.partId,
    from_procedure_rev_ref: moveDocument.revisionId,
    from_section_id_ref: sourceSubstep.section_id_ref,
    from_step_id_ref: sourceSubstep.step_id_ref,
    from_substep_id_ref: sourceSubstep.substep_id_ref,
    // For "into" operations, to_procedure_tree_node_ref is omitted
    
    to_procedure_tree_node_ref: toProcedureTreeNodeRef,
    to_order: toOrder,
    to_type: 'substep',
    to_part_id_ref: targetDoc.partId,
    to_procedure_rev_ref: targetDoc.revisionId,
    to_section_id_ref: toSectionIdRef,
    to_step_id_ref: toStepIdRef,
    to_substep_id_ref: toSubstepIdRef, // null for "into", target substep ID for "above"/"below"
    direction,
    // direction_move_into is TRUE for all COPY operations (per payload analysis)
    direction_move_into: direction_move_into !== undefined ? direction_move_into : true,
    audit_trail_data,
  };


  if (nodeIndexing && Object.keys(nodeIndexing).length > 0) {
    payload.node_indexing = nodeIndexing;
  }

  return cleanupSubstepCopyPayload(payload);
};

/**
 * Build move payload for substep
 */
export const buildSubstepMovePayload = async ({
  sourceSubstep,
  moveDocument,
  targetDoc,
  position,
  movePosition,
  moveNode,
  toOrder,
  toProcedureTreeNodeRef,
  toSectionIdRef,
  toStepIdRef,
  toSubstepIdRef,
  direction,
  direction_move_into,
  moveComment,
  targetAllNodesOriginal,
  sourceAllNodes = null,
  newContent,
}) => {
  // Build action_to_show
  let action_to_show = ""

  // Build version content
  const versionContent = await buildSubstepMoveVersionContent({
    allRevisionNodes: targetAllNodesOriginal,
    newContent,
    moveType: position,
    revision: targetDoc.revision,
    status: targetDoc.status || 'Not Submitted',
    item_code_type: targetDoc.type,
    partNumber: targetDoc.partNumber,
    partDescription: targetDoc.description || '',
    targetSectionIndex: movePosition.targetSectionIndex !== undefined ? movePosition.targetSectionIndex : (movePosition.sectionIndex !== undefined ? movePosition.sectionIndex : 0),
    targetStepIndex: movePosition.targetIndex !== undefined ? movePosition.targetIndex : undefined,
    parentVersion: null, // Not used in substep payloads
  });

  const isDifferentStepMove =
    sourceSubstep.step_id_ref &&
    toStepIdRef &&
    sourceSubstep.step_id_ref !== toStepIdRef;


  // Build audit trail data
  // Note: Based on payload analysis, MOVE operations do NOT include parent_version in audit_trail_data
  // hash should be empty string
  const audit_trail_data = await getAuditTrailData(
    {
      action: 'SUBSTEP_MOVED',
      comment: moveComment,
      parent_version: null, // MOVE operations do NOT include parent_version (per payload analysis)
      version_content: versionContent,
    },
    false
  );
  
  // Override hash to empty string for move operations
  audit_trail_data.hash = '';

  // Validate toProcedureTreeNodeRef for above/below operations
  if (position !== 'into' && !toProcedureTreeNodeRef) {
    throw new Error('to_procedure_tree_node_ref is required for above/below operations');
  }

  const nodeIndexing = buildNodeIndexingForSubstepMove({
    sourceSubstep,
    sourceAllNodes: sourceAllNodes || targetAllNodesOriginal,
    newContent,
    targetDoc,
    position,
    movePosition,
    targetAllNodesOriginal,
  });

  audit_trail_data.version_content.action_to_show = Object.values(nodeIndexing)[0]?.title?.split("System Performs Operation")[0].trim();
  // Build base payload
  const payload = {
    from_procedure_tree_node_ref: sourceSubstep.tree_node_id,
    from_order: parseInt(sourceSubstep.sibling_order || sourceSubstep.procedure_tree?.sibling_order || '1', 10),
    from_type: 'substep',
    from_part_id_ref: moveDocument.partId,
    from_procedure_rev_ref: moveDocument.revisionId,
    from_section_id_ref: sourceSubstep.section_id_ref,
    from_step_id_ref: sourceSubstep.step_id_ref,
    from_substep_id_ref: sourceSubstep.substep_id_ref,
    // For "into" operations, to_procedure_tree_node_ref is omitted (will be removed by cleanup)
    // For "above/below" operations, to_procedure_tree_node_ref should always be included
    to_procedure_tree_node_ref: toProcedureTreeNodeRef,
    to_order: toOrder,
    to_type: 'substep',
    to_part_id_ref: targetDoc.partId,
    to_procedure_rev_ref: targetDoc.revisionId,
    to_section_id_ref: toSectionIdRef,
    to_step_id_ref: toStepIdRef,
    to_substep_id_ref: toSubstepIdRef, // null for "into", target substep ID for "above"/"below"
    direction_move_into: direction_move_into !== undefined ? direction_move_into : false,
    direction,
    create_copy: false,
    audit_trail_data,
  };


  if (nodeIndexing && Object.keys(nodeIndexing).length > 0) {
    payload.node_indexing = nodeIndexing;
  }

  return cleanupSubstepMovePayload(payload);
};

